/** @jsx jsx */

import { useState } from 'react';
import { get, isEmpty, size } from 'lodash';

import { Alert, Button, Colors } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import Docs from 'app/developer-v3/components/SidebarRight/Docs/Docs';
import { VersionStatus } from 'app/developer-v3/constants';
import { useQueryImplementationCheck } from 'app/developer-v3/hooks';

import ActivationMessage from './ActivationMessage';
import Details from './Details';
import Summary from './Summary';
import { MODES as SIDEBAR_RIGHT_MODES } from '../constants';
import { normalizeMode } from '../utils';
import { SIDEBAR_TITLES, SECTIONS } from './constants';
import { Body, Footer, Header, Wrapper } from '../Layout';

import type { SemVer } from 'app/developer-v3/types/version';

const Styles = {
  promptWrapper: css`
    padding: 15px;
  `,
  footer: css`
    border-top: 1px solid ${Colors.GrayWarm4};
    padding: 25px 15px 15px 15px;
    margin-bottom: 18px;
  `,
  passingMessage: css`
    padding: 15px;
  `,
};

const DEFAULT_CLICKED_TAG = Object.freeze({
  tagID: '',
  deepLink: null,
  iconType: '',
  violationType: '',
  checkType: '',
});

type Props = {
  appId: string;
  isPublished: boolean;
  mode: SIDEBAR_RIGHT_MODES;
  onClose: () => void;
  version: SemVer;
  versionStatus: VersionStatus;
};

function Validate({
  appId,
  isPublished,
  mode,
  onClose,
  version,
  versionStatus,
}: Props) {
  const [activeSection, setActiveSection] = useState<SECTIONS>(
    SECTIONS.SUMMARY
  );

  const [clickedTag, setClickedTag] = useState<typeof DEFAULT_CLICKED_TAG>(
    DEFAULT_CLICKED_TAG
  );

  const {
    data,
    error,
    isFetched,
    isLoading,
    refetch,
    remove,
  } = useQueryImplementationCheck({
    integrationId: Number(appId),
    mode: normalizeMode(mode),
    version,
  });
  const activationTasks = data?.activationTasks ?? {
    results: [],
    totalFailures: 0,
  };
  const errors = data?.errors ?? { results: [], totalFailures: 0 };
  const hasErrors = data?.hasErrors ?? false;
  const hasActivationTasks = data?.hasActivationTasks ?? false;
  const isPassing = data?.isPassing ?? true;
  const passes = data?.passes ?? [];
  const totalChecks = data?.totalChecks ?? 0;
  const warnings = data?.warnings ?? { results: [], totalFailures: 0 };
  const hasValidated = isFetched && !isLoading;

  const isSummaryActive = activeSection === SECTIONS.SUMMARY;
  const isViolationActive = !isEmpty(clickedTag.tagID);

  /**
   * When the user closes/goes back
   * we want to reset the previously active violation
   */
  const resetActiveViolation = () => {
    setActiveSection(SECTIONS.SUMMARY);
    setClickedTag(DEFAULT_CLICKED_TAG);
  };

  const backProps = isSummaryActive
    ? {}
    : isViolationActive
    ? {
        onHome: () => resetActiveViolation(),
        onBack: () => setClickedTag(DEFAULT_CLICKED_TAG),
      }
    : {
        onHome: () => resetActiveViolation(),
        onBack: () => resetActiveViolation(),
      };

  const isActivating = mode !== SIDEBAR_RIGHT_MODES.DEFAULT;

  const title = SIDEBAR_TITLES[activeSection];

  const handleDetailsClick = (item, iconType, checkType, violationType) => {
    setClickedTag({
      tagID: item.tag,
      deepLink: item.deepLink,
      iconType,
      violationType,
      checkType,
    });
  };

  return (
    <Wrapper>
      <Header onClose={onClose} title={title} {...backProps} />
      <Body padding={0}>
        {!isLoading && isActivating && (
          <div css={Styles.promptWrapper}>
            <ActivationMessage
              canAdvance={!hasErrors && !hasActivationTasks}
              id={appId}
              isSummaryActive={isSummaryActive}
              mode={mode}
              version={version}
            />
          </div>
        )}
        {error ? (
          <Alert status="error" title="Validation Error">
            There was a problem validating your integration. Please try
            re-validating using the button below.
          </Alert>
        ) : isSummaryActive ? (
          <Summary
            changeSection={setActiveSection}
            checks={{ activationTasks, errors, warnings }}
            hasValidated={hasValidated}
            isHelpVisible={!isActivating}
            isPassing={isPassing}
            isPublished={isPublished}
            isValidating={isLoading}
            passingCount={size(passes)}
            totalChecks={totalChecks}
            versionStatus={versionStatus}
          />
        ) : isViolationActive ? (
          <Docs
            appId={appId}
            checkId={clickedTag.tagID}
            checkType={clickedTag.checkType}
            deepLink={clickedTag.deepLink}
            iconType={clickedTag.iconType}
            version={version}
            violationType={clickedTag.violationType}
          />
        ) : (
          <Details
            checkType={activeSection}
            checkWrapper={get(
              { activationTasks, errors, warnings },
              activeSection
            )}
            handleDetailsClick={handleDetailsClick}
            isPublished={isPublished}
            isValidating={isLoading}
            versionStatus={versionStatus}
          />
        )}
        {!isViolationActive && !isLoading && (
          <footer css={Styles.footer}>
            <Button
              color={hasValidated ? 'secondary' : 'primary'}
              iconBefore="arrowRefresh"
              onClick={() => {
                // Remove the cached query first. If we don't and call refetch
                // straight, the cached query is fetched too quickly to display
                // the loading state.
                remove();
                refetch();
              }}
            >
              {hasValidated ? ' Re-run validation' : 'Run validation'}
            </Button>
          </footer>
        )}
      </Body>
      <Footer />
    </Wrapper>
  );
}

Validate.defaultProps = {
  mode: SIDEBAR_RIGHT_MODES.DEFAULT,
};

export default Validate;
